import './LegalPages.css';

export default function PrivacyPage() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <a href="/" className="legal-back">← Back to Home</a>
        
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: March 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Nexus AI Recommendations ("we", "our", or "us") is committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you use our Shopify application.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <h3>2.1 Store Information</h3>
          <p>When you install our app, we collect:</p>
          <ul>
            <li>Store domain and name</li>
            <li>Store owner email address</li>
            <li>Product catalog information (titles, descriptions, prices, images)</li>
            <li>Store settings relevant to recommendations</li>
          </ul>

          <h3>2.2 Customer Behavior Data</h3>
          <p>To provide personalized recommendations, we collect anonymized customer behavior:</p>
          <ul>
            <li>Product views and browsing patterns</li>
            <li>Cart additions and removals</li>
            <li>Purchase history (product IDs only)</li>
            <li>Click interactions with recommendation widgets</li>
          </ul>
          <p><strong>Important:</strong> We do not collect personal customer information such as names, emails, or payment details.</p>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information to:</p>
          <ul>
            <li>Generate personalized product recommendations</li>
            <li>Train and improve our machine learning recommendation models using <strong>only aggregated and anonymized Shopify store data</strong> (such as product views, cart activity, and purchase patterns)</li>
            <li>Provide analytics and insights to store owners</li>
            <li>Communicate important updates about our service</li>
            <li>Provide customer support</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Storage and Security</h2>
          <p>
            Your data is stored in secure cloud infrastructure with encryption at rest and in transit. 
            We implement industry-standard security measures including:
          </p>
          <ul>
            <li>TLS 1.3 encryption for all data transmission</li>
            <li>AES-256 encryption for data at rest</li>
            <li>Regular security audits and penetration testing</li>
            <li>Access controls and authentication requirements</li>
          </ul>
        </section>

        <section>
          <h2>5. Email Service</h2>
          <p>
            Our application uses SMTP-based email services to send transactional and marketing emails
            on behalf of store owners. This includes:
          </p>
          <ul>
            <li>Abandoned cart recovery emails</li>
            <li>Post-purchase recommendation emails</li>
            <li>Test emails for verifying email configuration</li>
          </ul>
          <p>
            Store owners can configure and disconnect their email settings at any time through the
            Email Settings page in our app.
          </p>
        </section>

        <section>
          <h2>6. Data Sharing</h2>
          <p>
            We do not sell, trade, or rent your data to third parties. We may share data only in the 
            following circumstances:
          </p>
          <ul>
            <li>With service providers who assist in operating our app (under strict confidentiality agreements), including cloud providers and email delivery services</li>
            <li>When required by law or to protect our legal rights</li>
            <li>In connection with a business transfer or acquisition</li>
          </ul>
        </section>

        <section>
          <h2>7. Data Retention</h2>
          <p>
            We retain your data according to the following policy:
          </p>
          <ul>
            <li><strong>Active installations:</strong> Data is retained for as long as your app is installed.</li>
            <li><strong>After uninstallation:</strong> Your store data is retained for <strong>60 days</strong> after uninstallation, allowing you to reinstall and recover your settings and recommendation models.</li>
            <li><strong>Permanent deletion:</strong> After 60 days, all store data is permanently and automatically deleted, including:
              <ul>
                <li>Store settings and configuration</li>
                <li>Customer behavior data</li>
                <li>Order history and analytics</li>
                <li>AI/ML recommendation models trained exclusively on aggregated Shopify store data</li>
                <li>API keys and authentication tokens</li>
              </ul>
            </li>
            <li><strong>Immediate deletion:</strong> You can request immediate deletion of all your data at any time by contacting us at <a href="mailto:support@shopicraft-solutions.com">support@shopicraft-solutions.com</a>.</li>
          </ul>
          <p>
            <strong>Note:</strong> Aggregated, anonymized statistical data may be retained for service improvement purposes, but this data cannot be linked back to your store or customers.
          </p>
        </section>

        <section>
          <h2>8. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access the data we hold about your store</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Export your data in a portable format</li>
            <li>Object to certain data processing</li>
          </ul>
          <p>To exercise these rights, contact us at <a href="mailto:support@shopicraft-solutions.com">support@shopicraft-solutions.com</a>.</p>
        </section>

        <section>
          <h2>9. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy, please contact us at:<br />
            <strong>Email:</strong> <a href="mailto:support@shopicraft-solutions.com">support@shopicraft-solutions.com</a>
          </p>
        </section>
      </div>
    </div>
  );
}